import Phaser from 'phaser';
import { THEME } from './theme.js';

export class Board {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.graphics = scene.add.graphics();
    this.scene.scale.on('resize', () => this.draw());
    this.draw();
  }

  // Responsive scale (1 on desktop, smaller on phones). Matches GameScene's
  // uiScale so the table rim, seat offsets, etc. shrink together. Keeping
  // desktop (s=1) identical to before by multiplying the old fixed offsets.
  private vpScale(): number {
    const { width, height } = this.scene.scale;
    return Math.max(0.45, Math.min(1, Math.min(width / 1180, height / 760)));
  }

  draw(): void {
    const { width, height } = this.scene.scale;
    const s = this.vpScale();
    const g = this.graphics;
    g.clear();

    // Dark wine radial background (covers Phaser canvas)
    g.fillStyle(0x2c0b0e, 1);
    g.fillRect(0, 0, width, height);
    g.fillStyle(0x5a1c20, 0.85);
    g.fillEllipse(width / 2, height / 2, width * 1.2, height * 1.4);

    const { x: cx, y: cy } = this.getCenter();
    const { rx, ry } = this.getRadii();

    // Helper: draw a "stadium" (rounded rectangle with semicircle ends).
    // Treats `pad` as how much wider/taller than the felt this layer is.
    const stadium = (pad: number, color: number, alpha = 1): void => {
      const p = pad * s;
      const w = rx * 2 + p;
      const h = ry * 2 + p;
      g.fillStyle(color, alpha);
      g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, h / 2);
    };

    // Drop shadow
    g.fillStyle(0x000000, 0.5);
    g.fillRoundedRect(
      cx - rx - 60 * s,
      cy - ry - 50 * s,
      rx * 2 + 120 * s,
      ry * 2 + 110 * s,
      (ry * 2 + 110 * s) / 2,
    );

    // Black leather rail (outer cushion) — three layers for depth
    stadium(96, THEME.rail);
    stadium(82, THEME.railLight);
    stadium(70, THEME.rail);

    // Warm wood ring just inside the rail
    stadium(54, THEME.wood);
    stadium(40, THEME.woodLight);
    stadium(28, THEME.woodHighlight);

    // Felt (deep blue, poker style)
    stadium(12, THEME.feltGreenDark);
    stadium(0, THEME.feltGreen);

    // Felt highlight pool (lighter blue oval near the top)
    g.fillStyle(THEME.feltGreenLight, 0.32);
    g.fillEllipse(cx, cy - ry * 0.3, rx * 1.45, ry * 0.55);

    // Subtle inner play ring
    g.lineStyle(2, 0xa0c8e8, 0.20);
    const ringW = rx * 2 - 30 * s;
    const ringH = ry * 2 - 30 * s;
    g.strokeRoundedRect(
      cx - ringW / 2,
      cy - ringH / 2,
      ringW,
      ringH,
      ringH / 2,
    );
  }

  getCenter(): { x: number; y: number } {
    const { width, height } = this.scene.scale;
    return { x: width / 2, y: height / 2 + 30 * this.vpScale() };
  }

  getRadii(): { rx: number; ry: number } {
    const { width, height } = this.scene.scale;
    const s = this.vpScale();
    // Decouple the radii so a wide-but-short (landscape phone) viewport gets a
    // long, flat table that fills the horizontal space instead of a small oval:
    //  - ry is bound by the available HEIGHT (table + seats must fit vertically)
    //  - rx grows with WIDTH, capped so the side seat badges stay on screen.
    const ry = Math.min((height - 250 * s) * 0.42, width * 0.2);
    const rx = Math.max(ry, Math.min(width / 2 - 170 * s, ry * 3.0));
    return { rx, ry };
  }

  // Seat position around the felt - kept on the inner edge of the table.
  // seatIndex 0 is bottom-center (where the local player sits).
  getSeatPosition(
    seatIndex: number,
    totalSeats: number,
  ): { x: number; y: number; angle: number; isBottom: boolean } {
    const { x: cx, y: cy } = this.getCenter();
    const { rx, ry } = this.getRadii();
    const s = this.vpScale();
    // seatIndex 0 is the local player (bottom-center, no badge). The opponents
    // are spread across the UPPER arc — clustered toward the top and the two
    // ends — leaving the bottom clear for the local hand.
    let theta: number;
    if (seatIndex === 0 || totalSeats <= 1) {
      theta = Math.PI / 2;
    } else {
      const bottomGap = 2.4; // ~137° kept empty at the bottom for the hand
      const arc = Math.PI * 2 - bottomGap;
      const t = (seatIndex - 0.5) / (totalSeats - 1);
      theta = Math.PI / 2 + bottomGap / 2 + t * arc;
    }
    // Push the seat badges further outside the rail so they don't overlap the felt.
    const seatRx = rx + 70 * s;
    const seatRy = ry + 55 * s;
    const x = cx + Math.cos(theta) * seatRx;
    let y = cy + Math.sin(theta) * seatRy;

    // Hard clamp so no seat ever crosses into the hand area.
    const { height } = this.scene.scale;
    const handReservedTop = height - 230 * s;
    if (y > handReservedTop) y = handReservedTop;

    return {
      x,
      y,
      angle: theta,
      isBottom: Math.abs(theta - Math.PI / 2) < 0.01,
    };
  }

  // Position where the cards a given seat just played should be laid down -
  // between that seat's badge and the center of the table.
  getPlayDropPosition(
    seatIndex: number,
    totalSeats: number,
  ): { x: number; y: number } {
    const { x: cx, y: cy } = this.getCenter();
    const { rx, ry } = this.getRadii();
    const offset = Math.PI / 2;
    const theta = offset + (seatIndex / totalSeats) * Math.PI * 2;
    const dropRx = rx * 0.5;
    const dropRy = ry * 0.55;
    return {
      x: cx + Math.cos(theta) * dropRx,
      y: cy + Math.sin(theta) * dropRy,
    };
  }
}
